const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  (window.location.hostname === 'localhost'
    ? 'http://localhost:4000/api'
    : '/api');

let authToken: string | null = null;
const tokenExpiryListeners = new Set<() => void>();

export function setAuthToken(token: string | null) {
  authToken = token;
}

export function subscribeTokenExpired(listener: () => void) {
  tokenExpiryListeners.add(listener);
  return () => {
    tokenExpiryListeners.delete(listener);
  };
}

function notifyTokenExpired() {
  tokenExpiryListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error('Token expiry listener failed', error);
    }
  });
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined)
  };

  if (authToken && !options.skipAuth) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const errorMessage = (data && data.message) || 'Request failed';
    if (
      response.status === 401 &&
      typeof errorMessage === 'string' &&
      errorMessage.toLowerCase().includes('invalid token')
    ) {
      notifyTokenExpired();
    }
    const error = new Error(errorMessage) as Error & { status?: number; data?: unknown };
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data as T;
}

function buildHttpError(message: string, status: number, data: unknown) {
  if (
    status === 401 &&
    typeof message === 'string' &&
    message.toLowerCase().includes('invalid token')
  ) {
    notifyTokenExpired();
  }
  const error = new Error(message) as Error & { status?: number; data?: unknown };
  error.status = status;
  error.data = data;
  return error;
}

/**
 * Sends multipart/form-data (file uploads). Unlike `request`, it does NOT set a
 * Content-Type header — the browser sets the multipart boundary automatically.
 */
export async function uploadRequest<T>(
  path: string,
  formData: FormData,
  options: { method?: string } = {}
): Promise<T> {
  const headers: Record<string, string> = {};
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'POST',
    headers,
    body: formData
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw buildHttpError((data && data.message) || 'Request failed', response.status, data);
  }
  return data as T;
}

/** Fetches a binary attachment (with auth) and returns the blob plus its filename. */
export async function downloadRequest(
  path: string
): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = {};
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw buildHttpError((data && data.message) || 'Download failed', response.status, data);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : 'download';
  return { blob, filename };
}

export async function streamRequest(
  path: string,
  body: unknown,
  onChunk: (content: string) => void,
  onDone: () => void,
  onError: (error: Error) => void
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
  } catch (err) {
    onError(err instanceof Error ? err : new Error('Network error'));
    return;
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    onError(new Error((data && data.message) || `Request failed (${response.status})`));
    return;
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) continue;
        const payload = trimmed.slice(6);
        if (payload === '[DONE]') {
          onDone();
          return;
        }
        try {
          const parsed = JSON.parse(payload);
          if (parsed.error) {
            onError(new Error(parsed.error));
            return;
          }
          if (parsed.content) {
            onChunk(parsed.content);
          }
        } catch {
          // skip malformed chunks
        }
      }
    }
    onDone();
  } catch (err) {
    onError(err instanceof Error ? err : new Error('Stream error'));
  }
}

export const apiBaseUrl = API_BASE_URL;
